import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { DateModule } from '@epicuro-next/ataraxia/date';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { PopoverModule } from '@epicuro-next/ataraxia/popover';
import { TooltipDirective } from '@epicuro-next/ataraxia/tooltip';
import { selectLanguage } from '@epicuro-next/platform/i18n';
import {
  formatDateToLocaleDate,
  formatDateToLocaleDateUtc,
} from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

export const DEFAULT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputDatePicker),
  multi: true,
};

@Component({
  selector: 'atx-input-date-picker',
  templateUrl: './input-date-picker.component.html',
  styleUrls: ['./input-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DEFAULT_VALUE_ACCESSOR],
  imports: [
    CommonModule,
    IconsModule,
    PopoverModule,
    ReactiveFormsModule,
    DateModule,
    TooltipDirective,
  ],
})
export class InputDatePicker implements ControlValueAccessor {
  @Input()
  public minDate = new Date('1920-01-01');

  @Input()
  public maxDate: Date;

  @Input()
  public startDate = new Date();

  @Input()
  public allowClear = false;

  @Input()
  public selectTime = false;

  @Input()
  public useUtcLocale = false;

  @Input()
  public placeholder = $localize`:InputDate|Input date placeholder@@inputDatePlaceholder:Select date`;

  @Input()
  public dateFormat = 'MMM d, yyyy';

  private readonly locale$ = this.store.pipe(select(selectLanguage));

  public inputControl = new UntypedFormControl();

  public disabled = false;

  constructor(private store: Store) {}

  public dateSelected(value: string) {
    if (!value) {
      this.clear();
      return;
    }
    this.setFormControlValue(value);
    this.onChange(value);
    this.onTouch(value);
  }

  /**
   * Function to trigger onChange
   */
  public onChange: (arg: string | undefined) => void = () => {};

  /**
   * Function to trigger onTouched
   */
  public onTouch: (arg: string | undefined) => void = () => {};

  /**
   * Sets the value of the input
   * @param value Value to set
   */
  public writeValue(value: string | undefined): void {
    if (!value) return;

    this.setFormControlValue(value);
  }

  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onChange
   */
  public registerOnChange(fn: (arg: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onTouched
   */
  public registerOnTouched(fn: (arg: string) => void): void {
    this.onTouch = fn;
  }

  public setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    if (disabled) {
      this.inputControl.disable();

      return;
    }

    this.inputControl.enable();
  }

  public clear() {
    this.inputControl.setValue(undefined);
    this.onChange(undefined);
    this.onTouch(undefined);
  }

  private setFormControlValue(value: string) {
    this.locale$.pipe(take(1)).subscribe((locale) => {
      const formattedDate = this.useUtcLocale
        ? formatDateToLocaleDateUtc(value, locale, this.dateFormat)
        : formatDateToLocaleDate(value, locale, this.dateFormat);

      this.inputControl.setValue(formattedDate);
    });
  }
}
